import { AudioManager } from "./audio/audio";
import { soundKeys } from "./audio/sounds";
import { I18n, type Language } from "./i18n/i18n";
import { startOfflineSession, startOnlineSession, type GameSession } from "./game/session";
import {
  createLoadingScreen,
  createMainMenu,
  exitFullscreenIfActive,
  replaceRoot,
  showError
} from "./ui/ui";
import {
  LocalSettingsStore,
  type SavedGameMode,
  type SavedProfile,
  type SavedSettings
} from "./storage/storage";

export class App {
  private readonly root: HTMLElement;
  private readonly settingsStore = new LocalSettingsStore();
  private readonly audio = new AudioManager();

  private settings: SavedSettings;
  private i18n: I18n;
  private session: GameSession | null = null;

  public constructor(root: HTMLElement) {
    this.root = root;
    this.settings = this.settingsStore.load();
    this.i18n = new I18n(this.settings.language);
    this.audio.setEnabled(this.settings.soundEnabled);
  }

  public start(): void {
    this.showMenu();
  }

  private showMenu(): void {
    exitFullscreenIfActive();
    this.stopSession();

    const menu = createMainMenu({
      settings: this.settings,
      i18n: this.i18n,
      onProfileChange: (profile: SavedProfile) => {
        this.settings = this.settingsStore.update({ profile });
      },
      onLanguageChange: (language: Language) => {
        this.i18n.setLanguage(language);
        this.settings = this.settingsStore.update({ language });
      },
      onSoundEnabledChange: (soundEnabled: boolean) => {
        this.audio.setEnabled(soundEnabled);
        this.settings = this.settingsStore.update({ soundEnabled });
      },
      onPlay: (mode: SavedGameMode, profile: SavedProfile, language: Language) => {
        void this.startSelectedMode(mode, profile, language);
      }
    });

    replaceRoot(this.root, menu.element);
  }

  private async startSelectedMode(
    mode: SavedGameMode,
    profile: SavedProfile,
    language: Language
  ): Promise<void> {
    if (profile.name.trim().length === 0) {
      return;
    }

    this.settings = this.settingsStore.update({
      profile,
      language,
      lastMode: mode
    });

    this.i18n.setLanguage(language);
    this.audio.setEnabled(this.settings.soundEnabled);

    if (this.settings.soundEnabled) {
      await this.audio.unlock();
      this.audio.play(soundKeys.buttonClick);
    }

    replaceRoot(
      this.root,
      createLoadingScreen(
        this.i18n.t("app.title"),
        mode === "online" ? this.i18n.t("status.connecting") : this.i18n.t("status.offline")
      )
    );

    try {
      this.stopSession();

      const common = {
        root: this.root,
        profile,
        i18n: this.i18n,
        audio: this.audio,
        onReturnToMenu: () => this.showMenu()
      };

      this.session =
        mode === "online"
          ? await startOnlineSession(common)
          : await startOfflineSession(common);
    } catch (error) {
      showError(this.root, error instanceof Error ? error.message : this.i18n.t("error.generic"));
    }
  }

  private stopSession(): void {
    const session = this.session;
    this.session = null;
    session?.stop();
  }
}